import { describeRoute, type Route, type RouteAction } from '@cyberscore/contracts';
import { ArrowDown, ArrowUp, GitBranch, Plus, Trash2 } from 'lucide-react';
import { ConditionEditor } from './ConditionEditor';
import type { Cond, QuestionInfo } from './conditions';
import { move } from './journey';

/**
 * A stage's routes (task 85): after one of its questions is answered, and when a condition holds, skip
 * ahead. Routes only go forward; several after one question are tried in order.
 */

const ACTIONS: { value: RouteAction['action']; label: string }[] = [
  { value: 'goto', label: 'Go on to a later question' },
  { value: 'skip_questions', label: 'Skip these questions' },
  { value: 'end_stage', label: 'Skip the rest of this stage' },
  { value: 'skip_stages', label: 'Skip later stages' },
  { value: 'end_journey', label: 'End the assessment here' },
];

/** A fresh action of a kind, keeping what still makes sense from the old one. */
function actionOf(kind: RouteAction['action'], later: string[], stages: string[]): RouteAction {
  switch (kind) {
    case 'goto':
      return { action: 'goto', question: later[1] ?? later[0] ?? '' };
    case 'skip_questions':
      return { action: 'skip_questions', questions: later.slice(0, 1) };
    case 'skip_stages':
      return { action: 'skip_stages', stages: stages.slice(0, 1) };
    default:
      return { action: kind };
  }
}

export function RoutesEditor({
  stageKey,
  questions,
  routes,
  later,
  name,
  info,
  titles,
  editable,
  onChange,
}: {
  stageKey: string;
  /** The stage's questions, in order. */
  questions: string[];
  routes: Route[];
  /** Stages that wait for this one: what its routes may skip. */
  later: string[];
  name: (stage: string) => string;
  info: readonly QuestionInfo[];
  titles: Map<string, string>;
  editable: boolean;
  onChange: (routes: Route[]) => void;
}) {
  const set = (i: number, r: Route) => onChange(routes.map((x, k) => (k === i ? r : x)));
  const add = () =>
    onChange([
      ...routes,
      { after: questions[0] ?? '', then: { action: 'end_stage' }, skipped_as: 'not_asked', label: '' },
    ]);

  return (
    <div className="field">
      <span className="field-label row gap">
        <GitBranch size={14} /> Routes ({routes.length})
      </span>
      <p className="muted small">
        After a question is answered, skip ahead. Routes only go forward; the first that holds is taken.
      </p>
      {routes.length > 0 && (
        <ol className="route-rows">
          {routes.map((r, i) => (
            <RouteRow
              key={i}
              n={i + 1}
              route={r}
              stageKey={stageKey}
              questions={questions}
              later={later}
              name={name}
              info={info}
              titles={titles}
              editable={editable}
              first={i === 0}
              last={i === routes.length - 1}
              onChange={(x) => set(i, x)}
              onMove={(d) => onChange(move(routes, i, i + d))}
              onRemove={() => onChange(routes.filter((_, k) => k !== i))}
            />
          ))}
        </ol>
      )}
      {editable && questions.length > 1 && (
        <button type="button" className="btn btn-sm" onClick={add}>
          <Plus size={14} /> Add a route
        </button>
      )}
    </div>
  );
}

function RouteRow({
  n,
  route: r,
  stageKey,
  questions,
  later,
  name,
  info,
  titles,
  editable,
  first,
  last,
  onChange,
  onMove,
  onRemove,
}: {
  n: number;
  route: Route;
  stageKey: string;
  questions: string[];
  later: string[];
  name: (stage: string) => string;
  info: readonly QuestionInfo[];
  titles: Map<string, string>;
  editable: boolean;
  first: boolean;
  last: boolean;
  onChange: (r: Route) => void;
  onMove: (d: -1 | 1) => void;
  onRemove: () => void;
}) {
  const at = questions.indexOf(r.after);
  const after = at < 0 ? [] : questions.slice(at + 1);
  const t = r.then;
  const title = (k: string) => `${k} · ${titles.get(k) ?? 'Unknown question'}`;
  // The condition editor starts a new test on the question the route follows.
  const condQuestions = [...info.filter((q) => q.key === r.after), ...info.filter((q) => q.key !== r.after)];
  const toggle = (list: string[], k: string, on: boolean) =>
    on ? [...list, k] : list.filter((x) => x !== k);
  const label = `Route ${n}`;

  return (
    <li className="route-row" aria-label={label}>
      <div className="row-between">
        <span className="eyebrow">
          {label}
          {r.label ? ` · ${r.label}` : ''}
        </span>
        {editable && (
          <span className="row">
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move route ${n} up`} disabled={first} onClick={() => onMove(-1)}>
              <ArrowUp size={13} />
            </button>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Move route ${n} down`} disabled={last} onClick={() => onMove(1)}>
              <ArrowDown size={13} />
            </button>
            <button type="button" className="btn btn-sm btn-ghost btn-icon" aria-label={`Remove route ${n}`} onClick={onRemove}>
              <Trash2 size={13} />
            </button>
          </span>
        )}
      </div>
      <fieldset disabled={!editable} className="plain stack-sm">
        <div className="field">
          <label htmlFor={`r${n}-label`}>Why (shown to people)</label>
          <input id={`r${n}-label`} className="input" placeholder="No automatic backups" value={r.label} onChange={(e) => onChange({ ...r, label: e.target.value })} />
        </div>
        <div className="field">
          <label htmlFor={`r${n}-after`}>After</label>
          <select id={`r${n}-after`} className="input" value={r.after} onChange={(e) => onChange({ ...r, after: e.target.value })}>
            {questions.slice(0, -1).map((q) => (
              <option key={q} value={q}>
                {title(q)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <span className="field-label">When</span>
          <ConditionEditor value={r.when as Cond | undefined} onChange={(v) => onChange({ ...r, when: v as Route['when'] })} questions={condQuestions} mode="question" disabled={!editable} emptyLabel="Always, once it is answered" />
        </div>
        <div className="field">
          <label htmlFor={`r${n}-then`}>Then</label>
          <select id={`r${n}-then`} className="input" value={t.action} onChange={(e) => onChange({ ...r, then: actionOf(e.target.value as RouteAction['action'], after, later) })}>
            {ACTIONS.filter((a) => a.value !== 'skip_stages' || later.length).map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </select>
        </div>
        {t.action === 'goto' && (
          <div className="field">
            <label htmlFor={`r${n}-to`}>Go on to</label>
            <select id={`r${n}-to`} className="input" value={t.question} onChange={(e) => onChange({ ...r, then: { action: 'goto', question: e.target.value } })}>
              {after.map((q) => (
                <option key={q} value={q}>
                  {title(q)}
                </option>
              ))}
            </select>
          </div>
        )}
        {t.action === 'skip_questions' && (
          <fieldset className="plain">
            <legend className="field-label">Questions to skip</legend>
            <div className="route-picks">
              {/* The chosen ones first, so they stay in sight in a long stage. */}
              {[...new Set([...t.questions, ...after])].map((q) => (
                <label key={q} className="toggle-row small">
                  <input type="checkbox" checked={t.questions.includes(q)} onChange={(e) => onChange({ ...r, then: { action: 'skip_questions', questions: toggle(t.questions, q, e.target.checked) } })} /> {title(q)}
                </label>
              ))}
            </div>
          </fieldset>
        )}
        {t.action === 'skip_stages' && (
          <fieldset className="plain">
            <legend className="field-label">Stages to skip</legend>
            {later.map((k) => (
              <label key={k} className="toggle-row small">
                <input type="checkbox" checked={t.stages.includes(k)} onChange={(e) => onChange({ ...r, then: { action: 'skip_stages', stages: toggle(t.stages, k, e.target.checked) } })} /> {name(k)}
              </label>
            ))}
          </fieldset>
        )}
        <div className="field">
          <label htmlFor={`r${n}-as`}>Skipped questions count as</label>
          <select id={`r${n}-as`} className="input" value={r.skipped_as} onChange={(e) => onChange({ ...r, skipped_as: e.target.value as Route['skipped_as'] })}>
            <option value="not_asked">Not asked: they don’t apply</option>
            <option value="not_in_place">Not in place: answered with their “no”</option>
          </select>
        </div>
        <p className="muted small">
          After {r.after || '…'} in {stageKey}, {describeRoute(r, name)}.
        </p>
      </fieldset>
    </li>
  ); // prettier-ignore
}
